class PriveVideoBanner {
  constructor(root) {
    this.root = root;
    this.muteButton = root.querySelector('[data-prive-video-mute]');
    this.mediaBlocks = Array.from(root.querySelectorAll('[data-prive-video-media]'));
    this.mobileQuery = window.matchMedia('(max-width: 767px)');
    this.loadedVideos = new WeakSet();

    if (!this.mediaBlocks.length) return;

    this.bindMute();
    this.syncActiveBreakpoint(true);
    this.bindBreakpointChange();
  }

  isMobile() {
    return this.mobileQuery.matches;
  }

  getActiveMedia() {
    const shared = this.root.querySelector('.prive-video__player--shared');
    if (shared) return shared;

    const breakpoint = this.isMobile() ? 'mobile' : 'desktop';
    return (
      this.root.querySelector(`[data-prive-video-breakpoint="${breakpoint}"]`) ||
      this.mediaBlocks[0]
    );
  }

  getActiveVideos() {
    const activeMedia = this.getActiveMedia();
    if (!activeMedia) return [];
    return Array.from(activeMedia.querySelectorAll('video'));
  }

  unloadMedia(media) {
    if (!media) return;
    const video = media.querySelector('video');
    if (!video) return;

    video.pause();
    video.muted = true;
    video.removeAttribute('autoplay');
    video.preload = 'none';

    video.querySelectorAll('source').forEach((source) => {
      if (source.src && !source.dataset.src) {
        source.dataset.src = source.getAttribute('src');
      }
      source.removeAttribute('src');
    });

    if (video.src && !video.dataset.src) {
      video.dataset.src = video.getAttribute('src');
      video.removeAttribute('src');
    }

    try {
      video.load();
    } catch (e) {
      /* ignore */
    }

    media.removeAttribute('data-prive-video-active');
  }

  loadMedia(media) {
    if (!media) return;
    const video = media.querySelector('video');
    if (!video) return;

    const poster = media.dataset.priveVideoPoster;
    if (poster && !video.getAttribute('poster')) {
      video.setAttribute('poster', poster);
    }

    video.querySelectorAll('source').forEach((source) => {
      if (!source.getAttribute('src') && source.dataset.src) {
        source.setAttribute('src', source.dataset.src);
      }
    });

    if (!video.getAttribute('src') && video.dataset.src) {
      video.setAttribute('src', video.dataset.src);
    }

    video.preload = 'auto';
    video.setAttribute('autoplay', '');
    video.muted = true;
    media.setAttribute('data-prive-video-active', '');

    if (!this.loadedVideos.has(video)) {
      try {
        video.load();
      } catch (e) {
        /* ignore */
      }
      this.loadedVideos.add(video);
    }

    video.play().catch(() => {});
  }

  syncActiveBreakpoint(enableSoundAttempt) {
    const activeMedia = this.getActiveMedia();

    this.mediaBlocks.forEach((media) => {
      if (media === activeMedia) {
        this.loadMedia(media);
      } else {
        this.unloadMedia(media);
      }
    });

    this.syncMuteButton(true);

    if (enableSoundAttempt) {
      this.tryEnableSound();
    }
  }

  bindBreakpointChange() {
    const handler = () => this.syncActiveBreakpoint(false);

    if (typeof this.mobileQuery.addEventListener === 'function') {
      this.mobileQuery.addEventListener('change', handler);
    } else if (typeof this.mobileQuery.addListener === 'function') {
      this.mobileQuery.addListener(handler);
    }
  }

  tryEnableSound() {
    const activeVideos = this.getActiveVideos();
    if (!activeVideos.length) return;

    let unmutedAny = false;

    activeVideos.forEach((video) => {
      video.muted = false;
      video.volume = 1;

      const playPromise = video.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise
          .then(() => {
            if (!video.muted) unmutedAny = true;
            this.syncMuteButton(!unmutedAny && video.muted);
          })
          .catch(() => {
            video.muted = true;
            video.play().catch(() => {});
            this.syncMuteButton(true);
          });
      } else if (!video.muted) {
        unmutedAny = true;
      }
    });

    if (unmutedAny) this.syncMuteButton(false);
  }

  bindMute() {
    if (!this.muteButton) return;

    this.muteButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      const activeVideos = this.getActiveVideos();
      const currentlyMuted = activeVideos.every((video) => video.muted);
      const nextMuted = !currentlyMuted;

      activeVideos.forEach((video) => {
        video.muted = nextMuted;
        if (!nextMuted) {
          video.volume = 1;
          video.play().catch(() => {});
        }
      });

      this.syncMuteButton(nextMuted);
    });
  }

  syncMuteButton(isMuted) {
    if (!this.muteButton) return;
    this.muteButton.classList.toggle('is-unmuted', !isMuted);
    this.muteButton.setAttribute('aria-label', isMuted ? 'Unmute video' : 'Mute video');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-prive-video-root]').forEach((root) => {
    new PriveVideoBanner(root);
  });
});

window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  document.querySelectorAll('[data-prive-video-root]').forEach((root) => {
    const active = root.querySelector('[data-prive-video-active] video');
    if (active && active.paused) {
      active.play().catch(() => {});
    }
  });
});
